import React, { useEffect, useState } from "react";
import { db } from "../../lib/firebase";
import { collection, getDocs, getCountFromServer, query, where, doc, getDoc, setDoc, Timestamp } from "firebase/firestore";
import { 
  LuUsers, 
  LuDollarSign, 
  LuCalendar, 
  LuTrendingUp,
  LuActivity,
  LuSettings,
  LuUser,
  LuRefreshCw
} from "react-icons/lu";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { InputField } from "@cakrawalajakarta/shared-components";

const API_BASE = import.meta.env.VITE_API_BASE_URL;

export const Dashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState({
    events: [{
      id: "ULTRA24",
      title: "ULTRA24",
      stats: {
        totalCount: 0,
        verifiedCount: 0,
        totalRevenue: 0,
        totalNetRevenue: 0,
        totalRevenueUSD: 0,
        categories: [
          { name: "24H SOLO", maleCount: 0, femaleCount: 0, verifiedCount: 0, revenue: 0, netRevenue: 0, revenueUSD: 0, netRevenueUSD: 0 },
          { name: "12H SOLO", maleCount: 0, femaleCount: 0, verifiedCount: 0, revenue: 0, netRevenue: 0, revenueUSD: 0, netRevenueUSD: 0 },
          { name: "6H SOLO", maleCount: 0, femaleCount: 0, verifiedCount: 0, revenue: 0, netRevenue: 0, revenueUSD: 0, netRevenueUSD: 0 },
          { name: "RELAY 4x6H", maleCount: 0, femaleCount: 0, verifiedCount: 0, revenue: 0, netRevenue: 0, revenueUSD: 0, netRevenueUSD: 0 },
          { name: "100 KM", maleCount: 0, femaleCount: 0, verifiedCount: 0, revenue: 0, netRevenue: 0, revenueUSD: 0, netRevenueUSD: 0 },
          { name: "50 KM", maleCount: 0, femaleCount: 0, verifiedCount: 0, revenue: 0, netRevenue: 0, revenueUSD: 0, netRevenueUSD: 0 }
        ]
      }
    }],
    globalStats: {
      totalParticipants: 0,
      verifiedParticipants: 0,
      paidVerifiedCount: 0,
      bankVerifiedCount: 0,
      paypalVerifiedCount: 0,
      freeVerifiedCount: 0,
      totalRevenue: 0,
      totalNetRevenue: 0,
      totalRevenueUSD: 0,
      totalNetRevenueUSD: 0,
      totalGrossUSD: 0,
      categoryUpgradeRevenue: 0,
      totalWithdrawals: 0,
      totalBalance: 0,
      activeUsers: 0
    },
    loading: true
  });
  const [settingsModal, setSettingsModal] = useState({
    isOpen: false,
    eventId: null,
    enabled: false,
    deadline: ""
  });
  const [savingSettings, setSavingSettings] = useState(false);
  const [sweeping, setSweeping] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [forceSyncId, setForceSyncId] = useState("");
  const [isFullScan, setIsFullScan] = useState(false);
  const [syncLogs, setSyncLogs] = useState([]); // Logs from sync process

  const currentDate = new Date().toLocaleDateString('id-ID', { 
    weekday: 'long', 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });
  
  const { getIdToken } = useAuth();

  const fetchTransferConfig = async (eventId) => {
    try {
      const token = await getIdToken();
      const { data } = await axios.get(`${API_BASE}/api/category-transfer/config/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return {
        enabled: Boolean(data.enabled),
        deadline: data.deadline || ""
      };
    } catch (error) {
      if (error.response?.status && error.response.status !== 404) {
        throw error;
      }

      const eventSnap = await getDoc(doc(db, "semuaKegiatan", eventId));
      const config = eventSnap.exists() ? eventSnap.data()?.categoryTransferConfig : null;
      return {
        enabled: Boolean(config?.enabled),
        deadline: config?.deadline || ""
      };
    }
  };

  const saveTransferConfig = async (eventId, settings) => {
    try {
      const token = await getIdToken();
      await axios.post(`${API_BASE}/api/category-transfer/config/${eventId}`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      if (error.response?.status !== 404) {
        throw error;
      }

      await setDoc(doc(db, "semuaKegiatan", eventId), {
        categoryTransferConfig: {
          eventId,
          enabled: Boolean(settings.enabled),
          deadline: settings.deadline || null,
          mode: "category_change",
          updatedAt: Timestamp.now(),
          updatedVia: "firestore_fallback"
        }
      }, { merge: true });
    }
  };

  // Upgrade requests only close on their own when a gateway notification comes
  // back, so requests that never reached checkout would otherwise stay open
  // forever and keep blocking the participant's "Change Category" button.
  const runExpireSweep = async () => {
    setSweeping(true);
    try {
      const token = await getIdToken();

      const { data: preview } = await axios.post(
        `${API_BASE}/api/category-transfer/expire-stale`,
        { dryRun: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!preview.expiredCount) {
        alert(`No stale upgrade requests found (scanned ${preview.scanned}).`);
        return;
      }

      const summary = preview.expired
        .map((item) => `• ${item.fromCategory || "?"} → ${item.toCategory || "?"} (${item.reason})`)
        .join("\n");

      const confirmed = window.confirm(
        `${preview.expiredCount} stale upgrade request(s) will be closed:\n\n${summary}\n\n` +
          `Participants keep their original paid category. Continue?`
      );
      if (!confirmed) return;

      const { data: result } = await axios.post(
        `${API_BASE}/api/category-transfer/expire-stale`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert(`Closed ${result.expiredCount} stale upgrade request(s).`);
    } catch (error) {
      alert("Failed to run sweep: " + (error.response?.data?.message || error.message));
    } finally {
      setSweeping(false);
    }
  };

  const fetchStats = async () => {
      try {
        // Parallelize initial top-level fetches
        const [usersSnapshot, eventsSnapshot, token] = await Promise.all([
          getCountFromServer(collection(db, "users")),
          getDocs(collection(db, "semuaKegiatan")),
          getIdToken()
        ]);

        const totalUsers = usersSnapshot.data().count;
        const allEvents = eventsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        const eventsList = allEvents.filter(event => event.id === "ULTRA24");

        // Parallelize Refunds and Withdrawals
        const [refundedOrderIds, totalWithdrawals] = await Promise.all([
          (async () => {
            const ids = new Set();
            try {
              const { data: refundData } = await axios.get(`${API_BASE}/api/export-refunds?eventId=ULTRA24`, {
                headers: { Authorization: `Bearer ${token}` }
              });
              if (Array.isArray(refundData)) {
                refundData.forEach((r) => { if (r.orderId) ids.add(r.orderId); });
              }
            } catch (error) {
              console.warn("Could not fetch refunded transactions via API.", error);
            }
            return ids;
          })(),
          (async () => {
             let withdrawals = 0;
             try {
                const withdrawalsSnapshot = await getDocs(query(collection(db, "withdrawals"), where("eventId", "==", "ULTRA24")));
                withdrawalsSnapshot.forEach(doc => {
                   const w = doc.data();
                   withdrawals += (Number(w.amount) || 0);
                });
             } catch (error) {
                console.warn("Could not fetch withdrawals.", error);
             }
             return withdrawals;
          })()
        ]);

        const detailedEventsPromises = eventsList.map(async (event) => {
          const collectionName = event.collectionName || (event.id === "ULTRA24" ? "pendaftaranUltra24" : `pendaftaran${event.id}`);
          const participantsRef = collection(db, collectionName);
          
          // OPTIMIZATION: Get total count cheaply and only fetch confirmed records to save 90% of reads
          const [totalCountSnap, settlementSnap, paypalSnap] = await Promise.all([
             getCountFromServer(participantsRef),
             getDocs(query(participantsRef, where("transaction_status", "in", ["settlement", "capture"]))),
             getDocs(query(participantsRef, where("paypalCapture.status", "==", "COMPLETED")))
          ]);

          // OPTIMIZATION: Get registration breakdown cheaply (1 read per category)
          const categoryStats = {};
          const definedCategories = event.categories || event.kategori || [];
          const categoryCountsPromises = definedCategories.map(async (p) => {
             const rawName = typeof p === 'object' ? (p.categoryName || p.nama_kategori || p.name || p.kategori) : p;
             const dist = typeof p === 'object' ? p.distance : null;
             
             let q;
             if (dist) {
                q = query(participantsRef, where("distance", "==", Number(dist)));
             } else {
                q = query(participantsRef, where("kategori", "==", rawName));
             }
             
             const snap = await getCountFromServer(q);
             return { name: rawName, count: snap.data().count };
          });
          const categoryCounts = await Promise.all(categoryCountsPromises);

          const pDataMap = new Map();
          settlementSnap.forEach(doc => pDataMap.set(doc.id, { id: doc.id, ...doc.data() }));
          paypalSnap.forEach(doc => {
             if (!pDataMap.has(doc.id)) {
                pDataMap.set(doc.id, { id: doc.id, ...doc.data() });
             }
          });

          let eventTotalCount = totalCountSnap.data().count;
          let eventVerifiedCount = 0;
          let eventPaidVerifiedCount = 0;
          let eventBankVerifiedCount = 0;
          let eventPaypalVerifiedCount = 0;
          let eventFreeVerifiedCount = 0;
          let eventTotalRevenue = 0;
          let eventTotalRevenueUSD = 0;
          let eventTotalNetRevenueUSD = 0;
          let eventTotalGrossUSD = 0;
          let eventNetRevenue = 0;
          


          // Initialize stats for ALL categories defined in the event
          // Check both 'categories' (from user dump) and 'kategori' (legacy)
          
          if (definedCategories && Array.isArray(definedCategories)) {
             definedCategories.forEach(p => {
                // Determine category name - handling the structure from user input
                const rawName = typeof p === 'object' ? (p.categoryName || p.nama_kategori || p.name || p.kategori) : p;
                
                // Use raw name directly
                const dispName = rawName;
                
                if (dispName) {
                    const regCount = categoryCounts.find(c => c.name === rawName)?.count || 0;
                    categoryStats[dispName] = { 
                       count: regCount, verifiedCount: 0, 
                       maleCount: 0, femaleCount: 0,
                        revenue: 0, netRevenue: 0,
                        revenueUSD: 0, netRevenueUSD: 0,
                        bankVerifiedCount: 0,
                        paypalVerifiedCount: 0,
                        freeVerifiedCount: 0,
                        // Keep raw name reference for matching participants later
                        rawName: rawName
                    };
                }
             });
          }

          pDataMap.forEach((p, docId) => {
             // STRICT EXCLUSION: Skip Refunds (Ignore if this specific record is already successful)
             if (p.transaction_status !== "settlement" && p.paypalCapture?.status !== 'COMPLETED' && (refundedOrderIds.has(p.orderId) || refundedOrderIds.has(docId))) {
                return;
             }

             let rawCategory = p.kategori || "Uncategorized";
             
             // MAP DISTANCE TO STANDARD NAMES (ULTRA24)
             // if (p.distance) {
             //    const dist = String(p.distance);
             //    if (dist === "100") rawCategory = "100 KM";
             //    else if (dist === "50") rawCategory = "50 KM";
             // }
             
             const category = rawCategory;

             const isSettled = p.transaction_status === "settlement";
             
             // GAS Logic (assuming relevant fields exist or defaulting)
             const isPayPalCompleted = p.paypalCapture?.status === 'COMPLETED';
             const isPayPalType = p.paymentMethod === 'paypal' || p.payment_type === 'paypal';
             const isPayPal = isPayPalCompleted || (isPayPalType && isSettled) || p.currency === "USD";
             const isFree = p.payment_type === 'free' || p.payment_type === 'free_voucher' || Number(p.gross_amount) === 0 || Number(p.harga) === 0 || !p.harga;


             if (!categoryStats[category]) {
                // Fallback for categories not in event definition or "Uncategorized"
                categoryStats[category] = { 
                   count: 0, verifiedCount: 0, paidVerifiedCount: 0, freeVerifiedCount: 0,
                   maleCount: 0, femaleCount: 0,
                   revenue: 0, netRevenue: 0,
                   revenueUSD: 0, netRevenueUSD: 0,
                   rawName: rawCategory
                };
             }

             // Count all participants already handled via categoryCounts for known categories
             // categoryStats[category].count += 1;

             if (isSettled || isPayPalCompleted) {
                // Verified Logic
                
                // Gender Counting
                const gender = (p.gender || p.jenis_kelamin || "").toLowerCase();
                if (gender.includes('male') || gender.includes('laki') || gender.includes('pria')) {
                    if (!gender.includes('fe') && !gender.includes('perempuan') && !gender.includes('wanita')) {
                         categoryStats[category].maleCount += 1;
                    } else {
                         categoryStats[category].femaleCount += 1;
                    }
                } else if (gender.includes('female') || gender.includes('perempuan') || gender.includes('wanita')) {
                    categoryStats[category].femaleCount += 1;
                }

                if (isPayPal) {
                   // PayPal Logic (USD)
                   let amountUSD = 0;
                   // Fallback: Use gross_amount if detailed info is missing but it's a USD/PayPal transaction
                   if (amountUSD === 0 && (p.currency === "USD" || isPayPal)) {
                      amountUSD = parseFloat(p.gross_amount || 0);
                   }

                   categoryStats[category].verifiedCount += 1;
                   eventVerifiedCount += 1;

                   categoryStats[category].paidVerifiedCount += 1;
                   eventPaidVerifiedCount += 1;
                   
                   categoryStats[category].paypalVerifiedCount += 1;
                   eventPaypalVerifiedCount += 1;
                   
                   // Revenue dicatat dalam IDR memakai nilai yang dikonversi saat
                   // create-order (setelah voucher). Record lama tanpa field itu
                   // jatuh ke harga kategori.
                   const amountIDR = p.paypalAmountIDR != null
                      ? Number(p.paypalAmountIDR)
                      : parseFloat(p.harga || 0);
                   eventTotalRevenueUSD += amountIDR;
                   categoryStats[category].revenueUSD += amountIDR;
                   eventTotalGrossUSD += amountUSD;

                   // Net USD: pakai net aktual dari PayPal (seller_receivable_breakdown).
                   // Estimasi 3.9% + $0.30 hanya fallback untuk record tanpa data itu.
                   const netUSD = p.paypalNetUSD != null
                      ? Number(p.paypalNetUSD)
                      : Math.max(0, amountUSD - ((amountUSD * 0.039) + 0.30));

                   eventTotalNetRevenueUSD += netUSD;
                   categoryStats[category].netRevenueUSD += netUSD;

                } else {
                   categoryStats[category].verifiedCount += 1;
                   eventVerifiedCount += 1;

                   if (!isFree) {
                      // Midtrans Logic (IDR)
                      categoryStats[category].paidVerifiedCount += 1;
                      eventPaidVerifiedCount += 1;

                      const amountIDR = Number(p.gross_amount) || Number(p.harga) || 0;
                      const midtransFee = 4440; // Hardcoded per user logic
                      
                      categoryStats[category].bankVerifiedCount += 1;
                      eventBankVerifiedCount += 1;
                      
                      eventTotalRevenue += amountIDR;
                      categoryStats[category].revenue += amountIDR;
                      
                      const netIDR = Math.max(0, amountIDR - midtransFee);
                      eventNetRevenue += netIDR;
                      categoryStats[category].netRevenue += netIDR;
                   } else {
                      categoryStats[category].freeVerifiedCount += 1;
                      eventFreeVerifiedCount += 1;
                   }
                }
             }
          });

          // Convert category map to array for easier rendering
          const categoriesArray = Object.entries(categoryStats).map(([name, stats]) => {
             // Custom sort order based on distance keywords in raw names
             let sortOrder = 99;
             const n = name.toUpperCase();
             if (n.includes('24H')) sortOrder = 1;
             else if (n.includes('12H')) sortOrder = 2;
             else if (n.includes('6H')) sortOrder = 3;
             else if (n.includes('RELAY')) sortOrder = 4;
             else if (n.includes('100')) sortOrder = 5;
             else if (n.includes('50')) sortOrder = 6;
             
             return {
                name,
                ...stats,
                sortOrder
             };
          }).sort((a, b) => a.sortOrder - b.sortOrder);

          return {
            ...event,
            stats: {
               totalCount: eventTotalCount,
               verifiedCount: eventVerifiedCount,
               paidVerifiedCount: eventPaidVerifiedCount,
               bankVerifiedCount: eventBankVerifiedCount,
               paypalVerifiedCount: eventPaypalVerifiedCount,
               freeVerifiedCount: eventFreeVerifiedCount,
               totalRevenue: eventTotalRevenue,
               totalNetRevenue: eventNetRevenue,
               totalRevenueUSD: eventTotalRevenueUSD,
               totalNetRevenueUSD: eventTotalNetRevenueUSD,
               totalGrossUSD: eventTotalGrossUSD,
               categories: categoriesArray
            }
          };
        });

        const detailedEvents = await Promise.all(detailedEventsPromises);

        // 3. Aggregate Global Stats
        const globalParticipants = detailedEvents.reduce((acc, curr) => acc + curr.stats.totalCount, 0);
        const globalVerifiedParticipants = detailedEvents.reduce((acc, curr) => acc + curr.stats.verifiedCount, 0);
        const globalPaidVerifiedParticipants = detailedEvents.reduce((acc, curr) => acc + curr.stats.paidVerifiedCount, 0);
        const globalBankVerifiedParticipants = detailedEvents.reduce((acc, curr) => acc + curr.stats.bankVerifiedCount, 0);
        const globalPaypalVerifiedParticipants = detailedEvents.reduce((acc, curr) => acc + curr.stats.paypalVerifiedCount, 0);
        const globalFreeVerifiedParticipants = detailedEvents.reduce((acc, curr) => acc + curr.stats.freeVerifiedCount, 0);
        // Category upgrade payments live in their own collection but the money
        // lands in the same Midtrans account (same logic as Finance page).
        let categoryUpgradeRevenue = 0;
        let categoryUpgradeNetRevenue = 0;
        try {
          const upgradeSnap = await getDocs(query(
            collection(db, "categoryTransferPayments"),
            where("eventId", "==", "ULTRA24"),
            where("transaction_status", "in", ["settlement", "capture"])
          ));
          upgradeSnap.forEach((docSnap) => {
            const u = docSnap.data();
            const amountIDR = Number(u.gross_amount) || Number(u.amountDue) || 0;
            if (amountIDR === 0 || u.financeExcluded || u.currency === "USD") return;
            categoryUpgradeRevenue += amountIDR;
            categoryUpgradeNetRevenue += Math.max(0, amountIDR - 4440);
          });
        } catch (error) {
          console.warn("Could not fetch category upgrade payments.", error);
        }

        const eventRevenue = detailedEvents.reduce((acc, curr) => acc + curr.stats.totalRevenue, 0);
        const eventNetRevenue = detailedEvents.reduce((acc, curr) => acc + curr.stats.totalNetRevenue, 0);
        const globalRevenue = eventRevenue + categoryUpgradeRevenue;
        const globalNetRevenue = eventNetRevenue + categoryUpgradeNetRevenue;
        const globalRevenueUSD = detailedEvents.reduce((acc, curr) => acc + curr.stats.totalRevenueUSD, 0);
        const globalNetRevenueUSD = detailedEvents.reduce((acc, curr) => acc + curr.stats.totalNetRevenueUSD, 0);
        const globalGrossUSD = detailedEvents.reduce((acc, curr) => acc + (curr.stats.totalGrossUSD || 0), 0);

        setData({
          events: detailedEvents,
          globalStats: {
            totalParticipants: globalParticipants,
            verifiedParticipants: globalVerifiedParticipants,
            paidVerifiedCount: globalPaidVerifiedParticipants,
            bankVerifiedCount: globalBankVerifiedParticipants,
            paypalVerifiedCount: globalPaypalVerifiedParticipants,
            freeVerifiedCount: globalFreeVerifiedParticipants,
            totalRevenue: globalRevenue,
            totalNetRevenue: globalNetRevenue,
            totalRevenueUSD: globalRevenueUSD,
            totalNetRevenueUSD: globalNetRevenueUSD,
            totalGrossUSD: globalGrossUSD,
            categoryUpgradeRevenue,
            totalWithdrawals: totalWithdrawals,
            totalBalance: globalNetRevenue - totalWithdrawals,
            activeUsers: totalUsers
          },
          loading: false
        });

      } catch (err) {
        console.error("Failed to fetch dashboard data:", err);
        setData(prev => ({ ...prev, loading: false }));
      }
    };



  useEffect(() => {
    fetchStats();
  }, []);

  const handleSync = async (specificIdsInput = "", directIds = null) => {
     setIsSyncing(true);
     try {
        const token = await getIdToken();
        let idsArray = [];
        
        if (directIds && Array.isArray(directIds)) {
           idsArray = directIds;
        } else if (specificIdsInput) {
           idsArray = specificIdsInput.split(/[\s,]+/)
              .filter(id => id.trim().startsWith("ORDER-"))
              .map(id => id.trim());
        }

        const response = await axios.post(`${API_BASE}/api/sync-transactions`, { 
           orderIds: idsArray.length > 0 ? idsArray : undefined,
           fullScan: isFullScan && idsArray.length === 0
        }, {
           headers: { Authorization: `Bearer ${token}` }
        });
        
        if (idsArray.length > 0) {
           // setForceSyncId(""); // Keep input or clear? Clear.
           setForceSyncId("");
        }
        
        if (response.data.logs) {
           setSyncLogs(response.data.logs);
        }

        // Refresh stats in background
        await fetchStats();
     } catch (error) {
        console.error("Sync failed:", error);
        alert("Sync failed: " + (error.response?.data?.message || error.message));
     } finally {
        setIsSyncing(false);
     }
  };


  // Helper for plain text IDR
  const formatIDR = (val) => `Rp ${(Number(val) || 0).toLocaleString('id-ID')}`;
  
  // Helper for styled IDR (last 3 digits small)
  const renderIDR = (val) => {
    const formatted = (Number(val) || 0).toLocaleString('id-ID');
    const parts = formatted.split('.');
    if (parts.length > 1) {
      const last = parts.pop();
      return (
        <span>
          Rp {parts.join('.')}.<span className="text-[0.75em] opacity-60 font-medium">{last}</span>
        </span>
      );
    }
    return `Rp ${formatted}`;
  };
  const formatUSD = (val) => `$${(Number(val) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div className="space-y-4 pb-10">
      {/* Hero Section */}
      {/* Hero Section */}
      <div className="bg-white rounded-xl p-4 border border-slate-200 text-slate-800 relative overflow-hidden">
         <div className="absolute top-0 right-0 p-4 opacity-5 text-slate-200 pointer-events-none">
            <LuActivity size={120} />
         </div>
         <div className="relative z-10">
            <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                <div>
                    <p className="text-slate-500 font-medium mb-1 flex items-center gap-2 text-xs uppercase tracking-wider">
                       <LuCalendar size={14} /> {currentDate}
                    </p>
                    <h1 className="text-2xl md:text-3xl font-bold mb-2 break-words">Good Morning, Admin 👋</h1>
                    <p className="text-slate-500 text-sm max-w-xl">
                       Overview and performance of <strong>ULTRA24</strong>
                    </p>
                </div>
            </div>
         </div>
      </div>

      {/* Global Stats Grid - Full Width */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 w-full">
        <StatsCard 
           title="Active Users" 
           value={data.globalStats.activeUsers.toLocaleString()} 
           icon={<LuUsers size={20} />}
           trend="Total accounts"
           color="bg-purple-500"
           subValue={`${data.globalStats.totalParticipants.toLocaleString()} Registered for Event`}
           onClick={() => navigate("/admin/users")}
        />
        <StatsCard 
           title="Verified Participants" 
           value={data.globalStats.verifiedParticipants.toLocaleString()} 
           icon={<LuUsers size={20} />}
           trend="Settlement Status"
           color="bg-blue-500"
           subValue={(
             <span className="opacity-75">
                {data.globalStats.bankVerifiedCount} Bank, {data.globalStats.paypalVerifiedCount} PayPal, {data.globalStats.freeVerifiedCount} Free
             </span>
           )}
        />
          <StatsCard 
             title="IDR Revenue" 
             value={renderIDR(data.globalStats.totalRevenue)} 
             icon={<LuDollarSign size={20} />}
             trend="Midtrans / Bank Transfer"
             color="bg-lime-500"
             subValue={`Net: ${formatIDR(data.globalStats.totalNetRevenue)}`}
             onClick={() => navigate("/admin/finance")}
          />
        <StatsCard
           title="PayPal Revenue"
           value={renderIDR(data.globalStats.totalRevenueUSD)}
           icon={<LuDollarSign size={20} />}
           trend="International"
           color="bg-sky-500"
           subValue={`Gross: ${formatUSD(data.globalStats.totalGrossUSD)} · Net: ${formatUSD(data.globalStats.totalNetRevenueUSD)}`}
           onClick={() => navigate("/admin/finance-paypal")}
        />
      </div>

      {/* SYNC LOGS AREA */}
      {(syncLogs.length > 0 || isSyncing) && (
         <div className="bg-white rounded-xl border border-slate-200 p-4 font-mono text-xs w-full">
             <div className="max-h-60 overflow-y-auto custom-scrollbar">
                 <p className="text-slate-800 font-bold mb-2 sticky top-0 bg-white pb-2 border-b border-slate-100 flex justify-between items-center z-10">
                    <span className="flex items-center gap-2">
                       <LuActivity className="text-lime-600 animate-pulse" size={14} /> 
                       System Output
                    </span>
                    <button onClick={() => setSyncLogs([])} className="text-[10px] text-slate-400 hover:text-slate-800 uppercase font-bold">Clear</button>
                 </p>
                 {syncLogs.map((log, i) => (
                     <div key={i} className={`mb-1 ${log.includes("ERROR") || log.includes("fail") || log.includes("❌") ? "text-red-500" : log.includes("SUCCESS") || log.includes("Verified") || log.includes("✅") ? "text-green-600" : log.includes("WARN") ? "text-amber-500" : "text-slate-600"}`}>
                        {log}
                     </div>
                 ))}
                 {!isSyncing && syncLogs.length > 0 && (
                    <p className="text-green-600 font-bold mt-2 pt-2 border-t border-slate-100">Operation Finished.</p>
                 )}
             </div>
         </div>
      )}

      {/* Detailed Event Performance */}
      <div className="space-y-4">
         <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-2">
            <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
               <LuActivity className="text-red-600" size={20} /> Event Performance
            </h2>
            
            {/* COMPACT SYNC BAR */}
            <div className="flex flex-wrap items-center gap-2 w-full md:w-auto justify-start md:justify-end">
                 {/* CSV Upload and Minimal Repair Input hidden per request
                 <label className="group h-8 w-10 flex items-center justify-center rounded-lg border border-slate-200 bg-white hover:bg-slate-50 cursor-pointer transition-all" title="Upload Midtrans CSV">
                     <input 
                         type="file" 
                         accept=".csv"
                         className="hidden"
                         onChange={async (e) => {
                             const file = e.target.files[0];
                             if (!file) return;
                             const text = await file.text();
                             const lines = text.split(/\r?\n/);
                             const ids = [];
                             let orderIdIndex = -1;
                             const header = lines[0].split(/[;,]/);
                             orderIdIndex = header.findIndex(h => h.toLowerCase().includes("order id") || h.toLowerCase().includes("order_id"));
                             if (orderIdIndex === -1) {
                                 alert("Could not find 'Order ID' column. Please check CSV format.");
                                 return;
                             }
                             lines.slice(1).forEach(line => {
                                 const cols = line.split(/[;,]/);
                                 if (cols[orderIdIndex]) {
                                     const cleanId = cols[orderIdIndex].replace(/["']/g, "").trim();
                                     if (cleanId.startsWith("ORDER-")) ids.push(cleanId);
                                 }
                             });
                             if (ids.length > 0) {
                                 if(confirm(`Found ${ids.length} Order IDs in CSV. Reconcile these records?`)) {
                                     handleSync(null, ids);
                                     e.target.value = null;
                                 }
                             } else {
                                 alert("No valid ORDER- IDs found in CSV.");
                             }
                         }}
                     />
                     <span className="text-[10px] font-black text-slate-400 group-hover:text-green-600">CSV</span>
                 </label>

                 <div className="flex items-center h-8 bg-white rounded-xl border border-slate-200 overflow-hidden">
                     <input 
                        type="text" 
                        placeholder="Order ID..." 
                        value={forceSyncId}
                        onChange={(e) => setForceSyncId(e.target.value)}
                        className="px-2 text-[12px] outline-none w-24 md:w-28 bg-transparent font-medium"
                     />
                     <button
                        onClick={() => handleSync(forceSyncId)}
                        disabled={isSyncing || !forceSyncId}
                        className="h-full px-2 text-[9px] font-black uppercase text-slate-400 hover:text-slate-900 bg-slate-50 border-l border-slate-100"
                     >
                        Repair
                     </button>
                 </div>
                 */}

                 {/* Sync Status Button */}
                 <button
                    onClick={() => handleSync()}
                    disabled={isSyncing}
                    className={`h-8 flex items-center gap-2 px-4 rounded-xl text-[10px] font-black uppercase transition-all border ${
                       isSyncing 
                       ? "bg-slate-100 text-slate-400 border-slate-200" 
                       : "bg-white text-slate-700 hover:bg-slate-50 border-slate-200"
                    }`}
                 >
                    <LuRefreshCw size={12} className={isSyncing ? 'animate-spin' : ''} />
                    {isSyncing ? 'Syncing' : 'Sync Status'}
                 </button>
            </div>
         </div>
         
         <div className="w-full space-y-4">
            {data.events.map((event) => (
               <div key={event.id} className="bg-white rounded-xl border border-slate-200 border-gray-200 overflow-hidden flex flex-col w-full">
                     <div className="p-4 border-b border-gray-100 bg-slate-50 flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
                        <div className="w-full md:w-auto">
                           <h3 className="font-bold text-base text-gray-900 line-clamp-1">{event.title || event.id}</h3>
                           <div className="flex flex-wrap items-center gap-2 mt-1">
                              <p className="text-xs text-gray-500">ID: {event.id}</p>
                              <button 
                                onClick={async () => {
                                  try {
                                    const data = await fetchTransferConfig(event.id);
                                    setSettingsModal({
                                      isOpen: true,
                                      eventId: event.id,
                                      enabled: data.enabled,
                                      deadline: data.deadline || ""
                                    });
                                  } catch (e) {
                                    // Fallback open with defaults if no config found or error
                                    setSettingsModal({
                                      isOpen: true,
                                      eventId: event.id,
                                      enabled: false,
                                      deadline: ""
                                    });
                                  }
                                }}
                                className="text-xs flex items-center gap-1 text-slate-500 hover:text-slate-800 bg-white border border-slate-200 px-2 py-0.5 rounded  transition-colors"
                              >
                                <LuSettings size={10} /> Config Transfer
                              </button>
                           </div>
                        </div>
                     
                     </div>
                                      <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4 md:hidden">
                        <div className="space-y-4">
                           {event.stats.categories.map((cat, idx) => (
                              <div key={idx} className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                                 <div className="flex justify-between items-start mb-3">
                                    <div className="flex items-center gap-2">
                                       <div className={`w-2 h-2 rounded-full ${cat.verifiedCount > 0 ? 'bg-amber-400' : 'bg-slate-300'}`}></div>
                                       <span className="font-bold text-slate-800 text-sm">{cat.name}</span>
                                    </div>
                                    <span className="bg-slate-800 text-white px-2 py-0.5 rounded-lg text-xs font-bold">
                                       {cat.verifiedCount} <span className="opacity-70 font-normal">Confirmed</span>
                                    </span>
                                 </div>
                                 
                                 <div className="grid grid-cols-2 gap-3 mb-3">
                                    <div className="bg-white p-2 rounded-lg border border-slate-100 text-center">
                                       <p className="text-[10px] text-slate-400 uppercase font-bold">Male</p>
                                       <p className="font-bold text-slate-700">{cat.maleCount}</p>
                                    </div>
                                    <div className="bg-white p-2 rounded-lg border border-slate-100 text-center">
                                       <p className="text-[10px] text-slate-400 uppercase font-bold">Female</p>
                                       <p className="font-bold text-slate-700">{cat.femaleCount}</p>
                                    </div>
                                 </div>

                                 <div className="space-y-2">
                                    <div className="flex justify-between items-center text-sm">
                                       <span className="text-slate-500">Revenue (IDR)</span>
                                       <div className="text-right">
                                          <p className="font-bold text-slate-800">{formatIDR(cat.revenue)}</p>
                                          <p className="text-[10px] text-slate-400">Net: {formatIDR(cat.netRevenue)}</p>
                                       </div>
                                    </div>
                                    <div className="flex justify-between items-center text-sm">
                                       <span className="text-slate-500">Revenue (PayPal)</span>
                                       <div className="text-right">
                                          <p className="font-bold text-sky-600">{formatIDR(cat.revenueUSD)}</p>
                                       </div>
                                    </div>
                                 </div>
                              </div>
                           ))}
                           {event.stats.categories.length === 0 && (
                              <div className="text-center text-slate-400 italic text-sm py-4">No data available</div>
                           )}
                           
                           <div className="border-t border-slate-200 pt-4 mt-2">
                              <h4 className="font-bold text-slate-700 mb-2">Total Event Performance</h4>
                              <div className="grid grid-cols-2 gap-3">
                                 <div>
                                    <p className="text-xs text-slate-500">Verified Participants</p>
                                    <p className="text-xl font-bold text-slate-900">{event.stats.verifiedCount.toLocaleString()}</p>
                                 </div>
                                 <div>
                                    <p className="text-xs text-slate-500">Total Revenue (IDR)</p>
                                    <p className="text-sm font-bold text-red-600">{formatIDR(event.stats.totalRevenue)}</p>
                                    <p className="text-[10px] text-slate-400">Net: {formatIDR(event.stats.totalNetRevenue)}</p>
                                 </div>
                              </div>
                           </div>
                        </div>
                     </div>

                  <div className="hidden md:block flex-1 p-0 overflow-x-auto">
                     <table className="w-full text-left border-collapse">
                        <thead>
                           <tr className="bg-gray-50 border-b border-gray-100">
                              <th className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">Category</th>
                              <th className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider text-center">Male</th>
                              <th className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider text-center">Female</th>
                              <th className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right">Confirmed</th>
                              <th className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right">Revenue (IDR)</th>
                              <th className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right">Revenue (PayPal)</th>
                           </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                           {event.stats.categories.map((cat, idx) => (
                              <tr key={idx} className="hover:bg-slate-50 transition-colors">
                                 <td className="px-4 py-3 text-sm font-medium text-gray-700">
                                    <div className="flex items-center gap-2">
                                       <div className={`w-1.5 h-1.5 rounded-full ${cat.verifiedCount > 0 ? 'bg-amber-400' : 'bg-slate-300'}`}></div>
                                       <div>
                                          <p className="font-bold">{cat.name}</p>
                                          <p className="text-xs text-gray-400">{cat.count} Registered</p>
                                       </div>
                                    </div>
                                 </td>
                                 <td className="px-4 py-3 text-sm text-gray-500 text-center">
                                    {cat.maleCount}
                                 </td>
                                 <td className="px-4 py-3 text-sm text-gray-500 text-center">
                                    {cat.femaleCount}
                                 </td>
                                 <td className="px-4 py-3 text-sm text-slate-800 text-right font-bold">
                                    {cat.verifiedCount.toLocaleString()}
                                 </td>
                                 <td className="px-4 py-2 text-sm text-gray-900 text-right">
                                    <div className="font-bold">{formatIDR(cat.revenue)}</div>
                                    <div className="text-xs text-gray-400">Net: {formatIDR(cat.netRevenue)}</div>
                                 </td>
                                 <td className="px-4 py-2 text-sm text-gray-900 text-right">
                                    <div className="font-bold">{formatIDR(cat.revenueUSD)}</div>
                                    <div className="text-xs text-gray-400">Net: {formatUSD(cat.netRevenueUSD)}</div>
                                 </td>
                              </tr>
                           ))}
                           {event.stats.categories.length === 0 && (
                              <tr>
                                 <td colSpan="4" className="px-4 py-4 text-center text-sm text-gray-400 italic">No settlement data available</td>
                              </tr>
                           )}
                        </tbody>
                        <tfoot className="bg-gray-50">
                           <tr>
                              <td className="px-4 py-3 text-sm font-bold text-gray-800">Total</td>
                              <td className="px-4 py-3 text-sm font-bold text-gray-800 text-center">
                                 {event.stats.categories.reduce((acc, c) => acc + c.maleCount, 0)}
                              </td>
                              <td className="px-4 py-3 text-sm font-bold text-gray-800 text-center">
                                 {event.stats.categories.reduce((acc, c) => acc + c.femaleCount, 0)}
                              </td>
                              <td className="px-4 py-3 text-sm font-bold text-gray-800 text-right">
                                 {event.stats.verifiedCount.toLocaleString()}
                              </td>
                              <td className="px-4 py-2 text-sm text-right">
                                 <div className="font-bold text-gray-800">{formatIDR(event.stats.totalRevenue)}</div>
                                 <div className="text-xs text-gray-400 font-semibold">Net: {formatIDR(event.stats.totalNetRevenue)}</div>
                              </td>
                               <td className="px-4 py-2 text-sm text-right">
                                 <div className="font-bold text-gray-800">{formatIDR(event.stats.totalRevenueUSD)}</div>

                              </td>
                           </tr>
                        </tfoot>
                     </table>
                  </div>
               </div>
            ))}
         </div>
      </div>

      {/* Settings Modal */}
      {settingsModal.isOpen && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4">
            <div 
                className="fixed inset-0 bg-black/50 backdrop-blur-sm" 
                onClick={() => setSettingsModal(prev => ({...prev, isOpen: false}))}
            />
          <div className="relative bg-white rounded-xl w-full max-w-md overflow-hidden">
             <div className="p-4 border-b border-slate-100 flex justify-between items-center">
                <h3 className="font-bold text-slate-800">Category Transfer Settings</h3>
                <button onClick={() => setSettingsModal(prev => ({...prev, isOpen: false}))} className="text-slate-400 hover:text-slate-600">&times;</button>
             </div>
             
             <div className="p-4 space-y-4">
                <div className="flex items-center justify-between">
                   <label className="text-sm font-medium text-slate-700">Enable Feature</label>
                   <button 
                      onClick={() => setSettingsModal(prev => ({...prev, enabled: !prev.enabled}))}
                      className={`w-12 h-6 rounded-full transition-colors relative ${settingsModal.enabled ? 'bg-lime-500' : 'bg-slate-200'}`}
                   >
                      <div className={`w-4 h-4 rounded-full bg-white absolute top-1 transition-all ${settingsModal.enabled ? 'left-7' : 'left-1'}`}></div>
                   </button>
                </div>

                <div>
                   <InputField
                      type="date"
                      label="Deadline (End Date)"
                      value={settingsModal.deadline}
                      onChange={(e) => setSettingsModal(prev => ({...prev, deadline: e.target.value}))}
                      labelColor="text-slate-700"
                      labelClassName="font-medium mb-1"
                   />
                   <p className="text-xs text-slate-500 mt-1">Participants cannot transfer after this date.</p>
                </div>

                <div className="pt-3 border-t border-slate-100">
                   <label className="text-sm font-medium text-slate-700 block mb-1">Stale Upgrade Requests</label>
                   <p className="text-xs text-slate-500 mb-2">
                      Closes approved requests that were never paid and requests never reviewed in time.
                      Participants keep their original paid category.
                   </p>
                   <button
                      type="button"
                      onClick={runExpireSweep}
                      disabled={sweeping}
                      className="w-full px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 rounded-xl transition-colors disabled:opacity-50"
                   >
                      {sweeping ? "Scanning..." : "Scan & close stale requests"}
                   </button>
                </div>
             </div>

             <div className="p-4 border-t border-slate-100 bg-slate-50 flex justify-end gap-2">
                <button 
                  onClick={() => setSettingsModal(prev => ({...prev, isOpen: false}))}
                  className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-200 rounded-xl transition-colors"
                >
                  Cancel
                </button>
                <button 
                  onClick={async () => {
                     setSavingSettings(true);
                     try {
                        await saveTransferConfig(settingsModal.eventId, {
                           enabled: settingsModal.enabled,
                           deadline: settingsModal.deadline
                        });
                        setSettingsModal(prev => ({...prev, isOpen: false}));
                        alert("Settings saved!");
                     } catch (e) {
                        alert("Failed to save settings: " + (e.response?.data?.message || e.message));
                     } finally {
                        setSavingSettings(false);
                     }
                  }}
                  disabled={savingSettings}
                  className="px-4 py-2 text-sm font-bold text-white bg-slate-900 hover:bg-slate-800 rounded-xl transition-colors disabled:opacity-50"
                >
                  {savingSettings ? "Saving..." : "Save Changes"}
                </button>
             </div>
          </div>
        </div>
      )}
    </div>
  );
};

const StatsCard = ({ title, value, icon, subValue, onClick }) => (
   <div 
     onClick={onClick}
     className={`bg-white p-4 rounded-xl border border-slate-200 relative ${onClick ? 'cursor-pointer' : ''}`}
   >
      <div className="absolute top-4 right-4 text-slate-400">
         {icon}
      </div>
      <div>
         <p className="text-slate-500 text-sm font-medium mb-1">{title}</p>
         <h3 className="text-3xl font-bold text-slate-800 mb-1">{value}</h3>
         <p className="text-sm text-slate-400 mt-1">
             {subValue ? subValue : <span className="text-slate-300">No data available</span>}
         </p>
      </div>
   </div>
);
